import { String } from 'core-js'
import { currencyDecimals, resolveCurrencyCode } from 'Schemas/currencies'
import { getLanguage } from './globalMethods'

export const getDecimals = (currency, defaultDecimalCount = 0) => {
  const currencyCode = resolveCurrencyCode(currency)
  const decimalCount =
    currencyCode && currencyDecimals[currencyCode] ? currencyDecimals[currencyCode][1] : defaultDecimalCount
  return decimalCount
}

/**  @function
 * * Int.NumberFormat wrapper. Localized currency pipe
 *
 * @name formatFiscalAmount
 * @param {(string|number)} amount - value to format
 * @param {string} currency - currency code
 * @param {boolean} symbol - whether to format with the currency symbol
 * @param {number} digits - fraction digits to use
 * @param {object} options - format options
 * @param {boolean} options.scale - whether amount is expressed in minor units
 * @param {('round'|'trunc')} options.roundingMode - fraction rounding mode
 * @returns {string}
 */
export const formatFiscalAmount = (
  amount,
  currency,
  symbol = true,
  digits = getDecimals(currency),
  options = {}
) => {
  if (!amount && amount !== 0) return ''
  if (!currency) return amount

  try {
    const currencyCode = resolveCurrencyCode(currency)
    const { scale = true, roundingMode = 'round' } = options || {}
    const factor = 10 ** digits
    const formattedAmount = scale ? amount / factor : amount
    const numericAmount = Number(formattedAmount)
    const amountToFormat =
      roundingMode === 'trunc'
        ? Math.trunc((numericAmount + Number.EPSILON) * factor) / factor
        : numericAmount

    return new Intl.NumberFormat(getLanguage(), {
      style: symbol ? 'currency' : 'decimal',
      currency: currencyCode,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    }).format(amountToFormat)
  } catch (e) {
    return amount
  }
}

/**  @function
 * * Phone number format pipe
 *
 * @name formatPhoneNumber
 * @param {(string|number)} number - value to format
 * @param {string} countryCode - country code
 * @returns {string}
 */
export const formatPhoneNumber = (number, countryCode) => {
  if (!number) return ''
  const twelveDigits = String(number).match(/^(\d{2})(\d{3})(\d{3})(\d{4})$/) // 391231234567
  const tenDigits = String(number).match(/^(\d{3})(\d{3})(\d{4})$/) // 3280987654

  if (tenDigits && countryCode) return `${countryCode}${tenDigits[1]}${tenDigits[2]}${tenDigits[3]}`
  if (twelveDigits) return `+${twelveDigits[1]}${twelveDigits[2]}${twelveDigits[3]}${twelveDigits[4]}`
  if (countryCode && number) return `${countryCode}${number}`

  return number
}

export const splitPhoneNumber = (phone = '', prefixes = []) => {
  const normalizedPhone = String(phone).replace(/\s/g, '')
  const sortedPrefixes = prefixes
    .map((prefixOption) => (typeof prefixOption === 'string' ? prefixOption : prefixOption?.prefix))
    .filter(Boolean)
    .sort((firstPrefix, secondPrefix) => secondPrefix.length - firstPrefix.length)
  const prefix = sortedPrefixes.find((phonePrefix) => normalizedPhone.startsWith(phonePrefix)) || ''

  return {
    prefix,
    number: normalizedPhone.slice(prefix.length).replace(/\D/g, '')
  }
}

export const getDividedAmount = (amount, currency) => amount / 10 ** getDecimals(currency)

export const getMultipliedAmount = (amount, currency) => Math.round(amount * 10 ** getDecimals(currency))

/**
 * Converts a numeric string with decimal/thousand separators (both IT and EN)
 * into a number without separators (e.g., "2.000,50" => 200050, "2,000.50" => 200050, "2 000,50" => 200050).
 *
 * @param {string} string
 * @returns {number}
 */
export const parseStringNumber = (string) => {
  if (!string) return NaN

  // Removes all non-numeric characters/separators
  const onlyDigits = string.replace(/[^\d]/g, '')

  // If the resulting string is not a number, return NaN
  if (!/^\d+$/.test(onlyDigits)) return NaN

  return Number(onlyDigits)
}

export const stripCurrencyFromAmount = (value) => {
  if (typeof value !== 'string') {
    return value
  }

  return value.replace(/[^\d.,]/g, '')
}

export const formatNumberString = (value, currency) => {
  if (typeof value !== 'string') {
    return value
  }

  const fractionDigits = getDecimals(currency, 2)
  //rimuovo ipotetici spazi da value
  const input = value.replace(/\s/g, '')

  // Trova l'ultimo separatore decimale
  const lastDot = input.lastIndexOf('.')
  const lastComma = input.lastIndexOf(',')
  let decimalSeparator = ''
  if (lastDot > lastComma) decimalSeparator = '.'
  else if (lastComma > lastDot) decimalSeparator = ','

  let normalizedInput
  if (
    decimalSeparator &&
    // Il separatore è decimale solo se ci sono cifre dopo
    ((decimalSeparator === '.' && /\.\d{1,2}$/.test(input)) || (decimalSeparator === ',' && /,\d{1,2}$/.test(input)))
  ) {
    // Rimuovi tutti i separatori di migliaia (tutti i punti o virgole che NON sono l'ultimo separatore)
    const integerPart = input.slice(0, Math.max(lastDot, lastComma)).replace(/[.,]/g, '')
    const decimalPart = input.slice(Math.max(lastDot, lastComma) + 1)
    normalizedInput = integerPart + '.' + decimalPart
  } else {
    // Nessun separatore decimale: rimuovi tutti i separatori e considera come intero
    normalizedInput = input.replace(/[.,]/g, '')
  }

  let number = parseFloat(normalizedInput)
  if (isNaN(number)) {
    return input
  }

  return new Intl.NumberFormat(getLanguage(), {
    style: currency ? 'currency' : 'decimal',
    ...(currency && {currency: currency}),
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    useGrouping: true
  }).format(number)
}

export const amountValidator = (value) => value === '' || value !== '0' || /^[0-9]*[.,]?[0-9]{0,2}$/.test(value)

export const formatAmountWithoutSymbol = (amount, currency) => {
  if (!amount) return ''
  const currencyCode = resolveCurrencyCode(currency)

  try {
    return new Intl.NumberFormat(getLanguage(), { currency: currencyCode }).format(amount / 10 ** getDecimals(currency))
  } catch (e) {
    return amount
  }
}

/**
 * calculates the propoprtion: amount is to arg2 as totalAmount is to 100
 * @name getPercentage
 * @param {string | number} amount - The amount to calculate the percentage for.
 * @param {string | number} totalAmount - The total amount to calculate the percentage against.
 * @returns {number}
 */
export const getPercentage = (amount, totalAmount) => {
  const amountNum = Number(amount)
  const totalAmountNum = Number(totalAmount)
  if (amount == 0) return 0
  if (!amount || !totalAmount || isNaN(amountNum) || isNaN(totalAmountNum)) return null
  const percent = (amountNum / totalAmountNum) * 100
  return +percent.toFixed(2)
}
export const handleOnlyNumberKeyDown = (e) => {
  // Bind paste handler once
  if (!e.target._onlyNumberPaste) {
    e.target.addEventListener('paste', (p) => {
      if (!/^[0-9.,]+$/.test(p.clipboardData.getData('text'))) p.preventDefault()
    })
    e.target._onlyNumberPaste = true
  }

  // Allow: navigation keys, decimals, common shortcuts
  if (
    [8, 9, 13, 37, 38, 39, 40, 46].includes(e.keyCode) ||
    [',', '.'].includes(e.key) ||
    ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase()))
  )
    return

  // Block non-numeric
  if (!/^[0-9]$/.test(e.key)) e.preventDefault()
}

// @generator utility:method